'use client';

import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { CouponItemDto } from '@musinsa/services-curator';
import { CURATION_SHOP_NOT_FOUND_ERROR_MESSAGE } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { notFound } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCuratorProxyEnabled } from '../../common';
import { FloatingButtonGroup, ScrollToTopButton } from '../../common/components';
import { useNavigate } from '../../common/hooks';
import { pathsMusinsa } from '../../common/paths/musinsa';
import { curatorQueries } from '../../query-factory';
import { useCouponCount } from '../hooks/useCouponCount';
import { useCouponList } from '../hooks/useCouponList';
import { useReceiveAllCoupons } from '../hooks/useReceiveAllCoupons';
import { useReceiveCoupon } from '../hooks/useReceiveCoupon';
import {
  trackAllCouponDownloadClickMusinsa,
  trackCouponDownloadClickMusinsa,
  trackMyShopCouponAppliedGoodsClickMusinsa,
  trackMyShopCouponScreenViewMusinsa,
  trackShopCouponAppliedGoodsClickMusinsa,
  trackShopCouponScreenViewMusinsa,
} from '../utils/couponMusinsaAnalytics';
import { CouponCard } from './CouponCard';
import { CouponDownloadAllButton } from './CouponDownloadAllButton';
import { CouponTitleSection } from './CouponTitleSection';
import { EmptyCouponView } from './EmptyCouponView';

interface CouponPageProps {
  shopSlug: string;
  isOwner?: boolean;
  className?: string;
}

export const CouponPage = ({ shopSlug, isOwner = false, className }: CouponPageProps) => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const curatorProxyEnabled = useCuratorProxyEnabled();
  const {
    data: userInfo,
    isLoading: isUserInfoLoading,
    isError: isUserInfoError,
  } = useQuery({
    ...curatorQueries.profile.userInfo,
    enabled: isOwner,
  });

  const shopTitle = isOwner ? (userInfo?.snsTitle ?? '') : shopSlug;

  const {
    data: profile,
    isError: isProfileError,
    error: profileError,
  } = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(shopTitle),
    enabled: !!shopTitle,
  });

  const {
    coupons,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isCouponLoading,
    isSuccess,
    isError: isCouponError,
  } = useCouponList({
    shopTitle,
    enabled: !!shopTitle,
  });
  const { availableCount, isLoading: isCountLoading } = useCouponCount({
    shopTitle,
    enabled: !!shopTitle,
  });

  const { receiveCoupon, isPending: isReceiving } = useReceiveCoupon({ shopTitle });
  const { receiveAllCoupons, isPending: isReceivingAll } = useReceiveAllCoupons({
    shopTitle,
  });

  const { navigate } = useNavigate();

  const receiveDisabled = isReceiving || isReceivingAll;
  const receiveAllDisabled = receiveDisabled || isCountLoading || availableCount === 0;

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMusinsa || !shopTitle) return;
    if (isOwner) {
      trackMyShopCouponScreenViewMusinsa({ curatorId: shopTitle });
      return;
    }
    trackShopCouponScreenViewMusinsa({ curatorId: shopTitle });
  }, [isMusinsa, isOwner, shopTitle]);

  const handleReceiveCoupon = useCallback(
    (coupon: CouponItemDto) => {
      if (isMusinsa) {
        trackCouponDownloadClickMusinsa({
          type: coupon.salesRate.toString(),
          brandId: coupon.brandId,
          couponName: coupon.couponName,
          curatorId: shopTitle,
        });
      }
      receiveCoupon(coupon.couponNo);
    },
    [isMusinsa, receiveCoupon, shopTitle],
  );

  const handleReceiveAllCoupons = useCallback(() => {
    if (isMusinsa) {
      trackAllCouponDownloadClickMusinsa({ curatorId: shopTitle });
    }
    receiveAllCoupons();
  }, [isMusinsa, receiveAllCoupons, shopTitle]);

  const handleClickAppliedGoods = useCallback(
    (coupon: CouponItemDto) => {
      if (!shopTitle) return;
      if (isMusinsa) {
        if (isOwner) {
          trackMyShopCouponAppliedGoodsClickMusinsa({
            brandId: coupon.brandId,
            couponId: coupon.couponNo,
            couponName: coupon.couponName,
            curatorId: shopTitle,
          });
        } else {
          trackShopCouponAppliedGoodsClickMusinsa({
            brandId: coupon.brandId,
            couponId: coupon.couponNo,
            couponName: coupon.couponName,
            couponType: coupon.salesRate.toString(),
            curatorId: shopTitle,
          });
        }
      }
      const path = isOwner
        ? pathsMusinsa.myShopCouponGoods({ shopSlug: shopTitle, couponNo: coupon.couponNo })
        : pathsMusinsa.shopCouponGoods({ shopSlug: shopTitle, couponNo: coupon.couponNo });
      navigate(path);
    },
    [isMusinsa, isOwner, navigate, shopTitle],
  );

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersection, { rootMargin: '600px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersection]);

  // 큐레이터를 찾을 수 없는 경우 not-found 페이지로 이동
  if (
    isProfileError &&
    profileError instanceof Error &&
    profileError.message === CURATION_SHOP_NOT_FOUND_ERROR_MESSAGE
  ) {
    return notFound();
  }

  // 방문자 쿠폰 목록 조회 실패 시 not-found 페이지로 이동
  if (!isOwner && isCouponError) {
    return notFound();
  }

  const isLoading = (isOwner && isUserInfoLoading) || isCouponLoading;
  const isEmpty = (!isLoading && isSuccess && coupons.length === 0) || (isOwner && isUserInfoError);

  const title = profile?.nickname ? `${profile.nickname} 할인 쿠폰` : '할인 쿠폰';

  return (
    <div
      className={twMerge('flex grow flex-col bg-[#F5F5F5]', className)}
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 68px)' }}
    >
      {!isOwner && <CouponTitleSection title={title} />}

      {isEmpty ? (
        <EmptyCouponView shopSlug={shopSlug} isOwner={isOwner} />
      ) : (
        <>
          <div className={twMerge('flex flex-col pb-[80px] pt-[8px]!', isOwner && 'pt-[8px]')}>
            {coupons.map((coupon) => (
              <CouponCard
                key={coupon.couponNo}
                coupon={coupon}
                snsTitle={shopTitle}
                onReceive={isOwner ? undefined : () => handleReceiveCoupon(coupon)}
                enableLinkCopy={isOwner && curatorProxyEnabled}
                onClickAppliedGoods={
                  curatorProxyEnabled ? () => handleClickAppliedGoods(coupon) : undefined
                }
                disabled={receiveDisabled}
              />
            ))}
            <div ref={loadMoreRef} />
          </div>

          {!isOwner && (
            <CouponDownloadAllButton
              availableCount={availableCount}
              onClick={handleReceiveAllCoupons}
              disabled={receiveAllDisabled}
            />
          )}

          <FloatingButtonGroup.Layout>
            {/* 방문자 케이스는 CouponDownloadAllButton(고정 약 80px + safe-area)을 피해 위로 올림 */}
            <FloatingButtonGroup.Container className={isOwner ? '' : 'mb-[80px]!'}>
              <ScrollToTopButton />
            </FloatingButtonGroup.Container>
          </FloatingButtonGroup.Layout>
        </>
      )}
    </div>
  );
};
